import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = 'https://app.recensement-enfantsnonproteges.fr';
const BLUES = ['#1a7fd4','#0a1f44','#4db3ff','#185fa5','#7fcfff','#05112b','#b5d4f4'];

export default function PageStatistiques() {
  const [stats, setStats] = useState({});
  const [agregats, setAgregats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/stats`),
      axios.get(`${API}/api/stats/agregats`),
    ]).then(([s, a]) => {
      setStats(s.data);
      setAgregats(a.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const violencesCounts = {};
  const etatsCount = {};
  const raisonsCount = {};
  const gardeCounts = {};

  agregats.forEach(f => {
    (f.type_violences || []).forEach(v => { violencesCounts[v] = (violencesCounts[v]||0)+1; });
    if (f.etat_plainte) etatsCount[f.etat_plainte] = (etatsCount[f.etat_plainte]||0)+1;
    if (f.raison_invoquee) raisonsCount[f.raison_invoquee] = (raisonsCount[f.raison_invoquee]||0)+1;
    if (f.garde_agresseur) gardeCounts[f.garde_agresseur] = (gardeCounts[f.garde_agresseur]||0)+1;
  });

  const violencesData = Object.entries(violencesCounts).map(([name, value]) => ({ name, value })).sort((a,b) => b.value-a.value);
  const etatsData = Object.entries(etatsCount).map(([name, value]) => ({ name: name.length > 22 ? name.substring(0,22)+'…' : name, value, fullName: name })).sort((a,b) => b.value-a.value);
  const raisonsData = Object.entries(raisonsCount).map(([name, value]) => ({ name, value })).sort((a,b) => b.value-a.value);
  const gardeData = Object.entries(gardeCounts).map(([name, value]) => ({ name: name.length > 20 ? name.substring(0,20)+'…' : name, value })).sort((a,b) => b.value-a.value);

  const COMPTEURS = [
    { key: 'total_enfants', label: 'Enfants concernés', red: false },
    { key: 'alertes_urgentes', label: 'Alertes urgentes', red: true },
    { key: 'total_preuves', label: 'Preuves déposées', red: false },
    { key: 'total_protectrices', label: 'Professionnels ayant signalé', red: false },
    { key: 'total_magistrats', label: 'Magistrats ayant traité', red: false },
    { key: 'total_agents_ase', label: 'Agents ASE impliqués', red: false },
    { key: 'total_dossiers_perdus', label: 'Dossiers perdus', red: true },
    { key: 'total_jugements', label: 'Jugements rendus', red: false },
    { key: 'total_dysfonctionnements', label: 'Dysfonctionnements', red: true },
    { key: 'total_enfant_a_parle', label: "Fois où l'enfant a parlé", red: false },
    { key: 'departements', label: 'Départements représentés', red: false },
    { key: 'total_fiches', label: 'Fiches total', red: false },
  ];

  const Section = ({ title, children }) => (
    <div style={{background:'#fff',borderRadius:'12px',padding:'24px',marginBottom:'20px',border:'1px solid #dde3f0'}}>
      <div style={{fontSize:'13px',fontWeight:'700',color:'#0a1f44',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'20px',borderBottom:'2px solid #1a7fd4',paddingBottom:'10px'}}>{title}</div>
      {children}
    </div>
  );

  if (loading) return <div style={{textAlign:'center',padding:'80px',color:'#888'}}>Chargement…</div>;

  return (
    <div style={{maxWidth:'1000px',margin:'0 auto',padding:'36px 24px 60px'}}>
      <h2 style={{fontSize:'26px',fontWeight:'800',color:'#0a1f44',textTransform:'uppercase',marginBottom:'4px'}}>Statistiques</h2>
      <div style={{fontSize:'13px',color:'#888',marginBottom:'28px'}}>Données issues des fiches publiées — mise à jour en temps réel</div>

      <Section title="Chiffres clés">
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'12px'}}>
          {COMPTEURS.map(c => (
            <div key={c.key} style={{background:'#f4f6fb',borderRadius:'10px',padding:'16px',textAlign:'center',border:'1px solid #dde3f0'}}>
              <div style={{fontSize:'28px',fontWeight:'800',color:c.red?'#e24b4a':'#1a7fd4',marginBottom:'6px'}}>{stats[c.key] || 0}</div>
              <div style={{fontSize:'11px',color:'#666',lineHeight:'1.35'}}>{c.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {agregats.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px',color:'#aaa',background:'#fff',borderRadius:'12px',border:'1px solid #dde3f0'}}>
          <div style={{fontSize:'32px',marginBottom:'12px'}}>📊</div>
          <div>Les graphiques apparaîtront dès que des fiches seront publiées.</div>
        </div>
      ) : (
        <>
          {violencesData.length > 0 && (
            <Section title="Types de violences">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={violencesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f4fb" />
                  <XAxis dataKey="name" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} />
                  <Tooltip />
                  <Bar dataKey="value" name="Fiches" fill="#1a7fd4" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Section>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
            {etatsData.length > 0 && (
              <Section title="État des plaintes">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={etatsData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,percent}) => `${(percent*100).toFixed(0)}%`}>
                      {etatsData.map((_, i) => <Cell key={i} fill={BLUES[i % BLUES.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n, p) => [v, p.payload.fullName || n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'8px'}}>
                  {etatsData.map((d,i) => <div key={i} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'#555'}}><div style={{width:'8px',height:'8px',borderRadius:'50%',background:BLUES[i%BLUES.length],flexShrink:0}}></div>{d.fullName}</div>)}
                </div>
              </Section>
            )}

            {raisonsData.length > 0 && (
              <Section title="Raisons invoquées par la justice">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={raisonsData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,percent}) => `${(percent*100).toFixed(0)}%`}>
                      {raisonsData.map((_, i) => <Cell key={i} fill={BLUES[i % BLUES.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'8px'}}>
                  {raisonsData.map((d,i) => <div key={i} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'10px',color:'#555'}}><div style={{width:'8px',height:'8px',borderRadius:'50%',background:BLUES[i%BLUES.length],flexShrink:0}}></div>{d.name}</div>)}
                </div>
              </Section>
            )}

            {gardeData.length > 0 && (
              <Section title="Garde accordée à l'agresseur">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={gardeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4fb" />
                    <XAxis type="number" tick={{fontSize:10}} />
                    <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={130} />
                    <Tooltip />
                    <Bar dataKey="value" name="Fiches" fill="#e24b4a" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            )}
          </div>
        </>
      )}
    </div>
  );
}
